import { LockIcon } from "@phosphor-icons/react/dist/csr/Lock";
import { WarningCircleIcon } from "@phosphor-icons/react/dist/csr/WarningCircle";
import { Link } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@volca/ui/components/ui/empty";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { useAuth } from "@/core/auth";
import { useBusiness } from "@/core/business";
import { editorError, errorStatus } from "@/payroll/editor/state";
import { PAY_BREAKDOWN_PATH } from "@/payroll/pay-breakdown/routes";
import { useWorkbookAccessSetup } from "@/payroll/workbook-access/use-workbook-access-setup";
import { isAdminOrOwner } from "@/user/utils";

type EditorUnavailableStateProps =
  | { reason: "load-failed"; error: unknown; onRetry: () => void }
  | { reason: "no-access"; error?: unknown };

export const EditorUnavailableState = (props: EditorUnavailableStateProps) => {
  const { user } = useAuth();
  const { businessId } = useBusiness();
  const { isUnconfigured } = useWorkbookAccessSetup(businessId);

  if (props.reason === "load-failed")
    return (
      <Empty
        className={emptyStateVariants({ layout: "workspace" })}
        role="alert"
      >
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <WarningCircleIcon />
          </EmptyMedia>
          <EmptyTitle>Couldn't load payroll rules</EmptyTitle>
          <EmptyDescription>{editorError(props.error)}</EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          <Button variant="outline" onClick={props.onRetry}>
            Try again
          </Button>
        </EmptyContent>
      </Empty>
    );

  const signedOut = errorStatus(props.error) === 401;
  return (
    <Empty className={emptyStateVariants({ layout: "workspace" })} role="alert">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <LockIcon />
        </EmptyMedia>
        <EmptyTitle>
          {signedOut
            ? "Your session expired"
            : "Payroll rules editing isn't available"}
        </EmptyTitle>
        <EmptyDescription>
          {signedOut
            ? "Sign in again to continue."
            : isUnconfigured
              ? "Workbook access isn't set up for this business yet. Contact support to set it up."
              : isAdminOrOwner(user?.role)
                ? "Ask Volca to turn on payroll rules editing for this business."
                : "Ask a manager of this business for access to payroll rules."}
        </EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        <Button
          variant="outline"
          nativeButton={false}
          render={<Link to={signedOut ? "/login" : PAY_BREAKDOWN_PATH} />}
        >
          {signedOut ? "Sign in" : "Open payroll"}
        </Button>
      </EmptyContent>
    </Empty>
  );
};
